
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ActivityLog } from '@/types/activity-logs';

export const useActivityLogs = (bookingId: string) => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivityLogs = async () => {
    try {
      console.log('[ACTIVITY-LOGS] Fetching activity logs for booking:', bookingId);
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('booking_activity_logs')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('[ACTIVITY-LOGS] Error fetching activity logs:', fetchError);
        throw fetchError;
      }

      console.log('[ACTIVITY-LOGS] Activity logs fetched:', data?.length || 0);
      // Cast the data to match our ActivityLog interface
      const typedData = (data || []).map(item => ({
        ...item,
        actor_type: item.actor_type as 'customer' | 'internal' | 'system',
        metadata: item.metadata as Record<string, any>
      })) as ActivityLog[];
      
      setActivityLogs(typedData);
    } catch (err) {
      console.error('[ACTIVITY-LOGS] Failed to fetch activity logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch activity logs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (bookingId) {
      fetchActivityLogs();
    }
  }, [bookingId]);

  // Set up real-time subscription for activity logs
  useEffect(() => {
    if (!bookingId) return;

    console.log('[ACTIVITY-LOGS] Setting up real-time subscription for booking:', bookingId);

    const subscription = supabase
      .channel(`booking_activity_${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'booking_activity_logs',
          filter: `booking_id=eq.${bookingId}`
        },
        (payload) => {
          console.log('[ACTIVITY-LOGS] Real-time update received:', payload);
          
          // Safely handle the payload
          if (payload.eventType === 'INSERT' && payload.new) {
            const newLog = {
              ...payload.new,
              actor_type: (payload.new.actor_type as 'customer' | 'internal' | 'system') || 'system',
              metadata: (payload.new.metadata as Record<string, any>) || {}
            } as ActivityLog;
            
            setActivityLogs(prev => [newLog, ...prev]);
          } else if (payload.eventType === 'UPDATE' && payload.new) {
            const updatedLog = {
              ...payload.new,
              actor_type: (payload.new.actor_type as 'customer' | 'internal' | 'system') || 'system',
              metadata: (payload.new.metadata as Record<string, any>) || {}
            } as ActivityLog;
            
            setActivityLogs(prev => 
              prev.map(log => 
                log.id === payload.new.id ? updatedLog : log
              )
            );
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setActivityLogs(prev => 
              prev.filter(log => log.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[ACTIVITY-LOGS] Cleaning up real-time subscription');
      subscription.unsubscribe();
    };
  }, [bookingId]);

  return {
    activityLogs,
    isLoading,
    error,
    refreshActivityLogs: fetchActivityLogs
  };
};
